import React, { useEffect } from 'react';

const HelpModal = ({ isOpen, onClose, title, content }) => {
  // Handle ESC key press
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  // Handle click outside modal
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div 
      className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 backdrop-blur-sm p-2 sm:p-4"
      onClick={handleBackdropClick}
    >
      <div className="bg-white rounded-2xl max-w-2xl w-full shadow-2xl animate-fade-in max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 sm:p-6 border-b border-slate-200 bg-gradient-to-r from-blue-50 to-purple-50">
          <div className="flex items-center gap-3">
            {/* DiAi Avatar */}
            <img 
              src="https://customer-assets.emergentagent.com/job_author-ai-beta/artifacts/lad4xvs5_DiAi_Image.jpg"
              alt="DiAi Assistant"
              className="w-10 h-10 sm:w-12 sm:h-12 rounded-full border-2 border-blue-300 shadow-md"
            />
            <div>
              <h3 className="text-lg sm:text-xl font-bold text-slate-900">
                {title}
              </h3>
              <p className="text-xs text-slate-600">DiAi is here to help</p>
            </div>
          </div>
          
          {/* Close Button */}
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600 transition-colors p-2 hover:bg-white rounded-full"
            aria-label="Close help modal"
          >
            <svg 
              className="w-5 h-5 sm:w-6 sm:h-6" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={2} 
                d="M6 18L18 6M6 6l12 12" 
              />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="p-4 sm:p-6 overflow-y-auto flex-1 scrollbar-thin scrollbar-thumb-slate-300 scrollbar-track-slate-100">
          <div className="prose prose-sm sm:prose max-w-none text-slate-700 space-y-4">
            {typeof content === 'string' ? (
              <div 
                dangerouslySetInnerHTML={{ __html: content }} 
                className="[&>h3]:text-lg [&>h3]:font-bold [&>h3]:text-slate-900 [&>h3]:mb-2 [&>h3]:mt-6 [&>h3:first-child]:mt-0 [&>p]:text-slate-700 [&>p]:leading-relaxed [&>p]:mb-3 [&>ul]:ml-4 [&>ul]:space-y-2 [&>ul>li]:text-slate-700 [&>ul>li]:leading-relaxed"
              />
            ) : (
              content
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 sm:p-6 border-t border-slate-200 bg-slate-50">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <p className="text-xs text-slate-500">
              Need more help? <a href="/help-center" className="text-blue-600 hover:text-blue-700 font-medium">View Full Documentation →</a>
            </p>
            <button
              onClick={onClose}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium whitespace-nowrap"
            >
              Got it!
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HelpModal;
